import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ConfigDataService } from '@app/_services/config-data.service';
import { AlertService } from '@app/_services';
import { Subject } from 'rxjs';
import { takeUntil, first } from 'rxjs/operators';
import { PrintInstruction } from '@app/_models/printInstruction';

@Component({
  selector: 'app-print-instruction-manager',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './print-instruction-manager.component.html',
  styleUrls: ['./print-instruction-manager.component.scss']
})
export class PrintInstructionManagerComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  printInstructions: PrintInstruction[] = [];
  filteredInstructions: PrintInstruction[] = [];
  selectedInstruction: PrintInstruction | null = null;
  
  searchForm: FormGroup;
  editForm: FormGroup;
  
  isLoading = false;
  isEditMode = false;
  showModal = false;
  
  // Filter options
  searchTerm = '';
  selectedCompany = 'all';
  selectedAggregateType = 'all';
  
  // Available options for dropdowns
  policyCompanies: string[] = [];
  
  aggregateTypes = [
    'CPA',
    'CPA-NIA',
    'CPA-REL',
    'CPA-CHI',
    'Hospital',
    'Sampoorna Suraksha Plan'
  ];
  
  companyImageMap = {
    'THE NEW INDIA ASSURANCE CO. LTD': 'assets/NewIndiaLogo.png',
    'CARE HEALTH INSURANCE LIMITED': 'assets/care.png',
    'IFFCO TOKIO GENERAL INSURANCE CO. LTD': 'assets/iffcotokio.png',
    'RELIANCE GENERAL INSURANCE LIMITED': 'assets/reliance.png'
  };

  constructor(
    private fb: FormBuilder,
    private configDataService: ConfigDataService,
    private alertService: AlertService
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    this.loadPolicyCompanies();
    this.loadPrintInstructions();
  }
  
  private loadPolicyCompanies(): void {
    this.configDataService.PolicyCompanies$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (companies: string[]) => {
          this.policyCompanies = companies || [];
        },
        error: (error) => {
          // Fallback to default companies if config is not available
          this.policyCompanies = [
            'THE NEW INDIA ASSURANCE CO. LTD',
            'CARE HEALTH INSURANCE LIMITED',
            'IFFCO TOKIO GENERAL INSURANCE CO. LTD',
            'RELIANCE GENERAL INSURANCE LIMITED'
          ];
          this.alertService.warn('Failed to load policy companies from config, using defaults');
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForms(): void {
    this.searchForm = this.fb.group({
      searchTerm: [''],
      company: ['all'],
      aggregateType: ['all']
    });

    this.editForm = this.fb.group({
      policyType: ['', [Validators.required]],
      printPage: [false],
      printPage2: [false],
      printPage3: [false],
      printPageFinance: [false],
      printPageCarCPA: [false],
      printOnlyFirstPage: [false],
      printPageGeneralCPA: [false],
      printPageIfcoTokio: [false],
      policyCompany: ['', [Validators.required]],
      policyCompanyImage: ['', [Validators.required]],
      maxAgeAllowed: [null, [Validators.min(1), Validators.max(100)]],
      minAgeAllowed: [null, [Validators.min(1), Validators.max(100)]],
      aggregatePolicyType: ['', [Validators.required]]
    });

    // Watch for company changes to auto-update image
    this.editForm.get('policyCompany')?.valueChanges.subscribe(company => {
      if (company && this.companyImageMap[company]) {
        this.editForm.patchValue({
          policyCompanyImage: this.companyImageMap[company]
        });
      }
    });

    // Watch for search form changes
    this.searchForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.applyFilters();
      });
  }

  loadPrintInstructions(): void {
    this.isLoading = true;
    this.configDataService.getConfig('PrintInstruction')
      .pipe(
        first(),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (data: PrintInstruction[]) => {
          this.printInstructions = data || [];
          this.filteredInstructions = [...this.printInstructions];
          this.isLoading = false;
        },
        error: (error) => {
          this.alertService.error('Failed to load print instructions');
          this.isLoading = false;
        }
      });
  }

  applyFilters(): void {
    const { searchTerm, company, aggregateType } = this.searchForm.value;
    
    this.filteredInstructions = this.printInstructions.filter(instruction => {
      const matchesSearch = !searchTerm || 
        instruction.policyType.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCompany = company === 'all' || 
        instruction.policyCompany === company;
      
      const matchesAggregateType = aggregateType === 'all' || 
        instruction.aggregatePolicyType === aggregateType;
      
      return matchesSearch && matchesCompany && matchesAggregateType;
    });
  }

  openCreateModal(): void {
    this.selectedInstruction = null;
    this.isEditMode = false;
    this.resetEditForm();
    this.showModal = true;
  }

  openEditModal(instruction: PrintInstruction): void {
    this.selectedInstruction = instruction;
    this.isEditMode = true;
    this.populateEditForm(instruction);
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
    this.selectedInstruction = null;
    this.resetEditForm();
  }

  private populateEditForm(instruction: PrintInstruction): void {
    this.editForm.patchValue({
      policyType: instruction.policyType,
      printPage: instruction.printPage,
      printPage2: instruction.printPage2,
      printPage3: instruction.printPage3,
      printPageFinance: instruction.printPageFinance,
      printPageCarCPA: instruction.printPageCarCPA,
      printOnlyFirstPage: instruction.printOnlyFirstPage,
      printPageGeneralCPA: instruction.printPageGeneralCPA,
      printPageIfcoTokio: instruction.printPageIfcoTokio || false,
      policyCompany: instruction.policyCompany,
      policyCompanyImage: instruction.policyCompanyImage,
      maxAgeAllowed: instruction.maxAgeAllowed,
      minAgeAllowed: instruction.minAgeAllowed,
      aggregatePolicyType: instruction.aggregatePolicyType
    });
  }

  private resetEditForm(): void {
    this.editForm.reset({
      printPage: false,
      printPage2: false,
      printPage3: false,
      printPageFinance: false,
      printPageCarCPA: false,
      printOnlyFirstPage: false,
      printPageGeneralCPA: false,
      printPageIfcoTokio: false
    });
  }

  onSubmit(): void {
    if (this.editForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    const formValue = this.editForm.value;
    let updatedInstructions: PrintInstruction[];

    if (this.isEditMode && this.selectedInstruction) {
      // Update existing instruction
      updatedInstructions = this.printInstructions.map(instruction =>
        instruction.policyType === this.selectedInstruction!.policyType
          ? { ...formValue }
          : instruction
      );
    } else {
      // Add new instruction
      if (this.printInstructions.some(inst => inst.policyType === formValue.policyType)) {
        this.alertService.error('Policy type already exists');
        return;
      }
      updatedInstructions = [...this.printInstructions, formValue];
    }

    this.savePrintInstructions(updatedInstructions);
  }

  deleteInstruction(instruction: PrintInstruction): void {
    if (confirm(`Are you sure you want to delete "${instruction.policyType}"?`)) {
      const updatedInstructions = this.printInstructions.filter(
        inst => inst.policyType !== instruction.policyType
      );
      this.savePrintInstructions(updatedInstructions);
    }
  }

  private savePrintInstructions(instructions: PrintInstruction[]): void {
    this.isLoading = true;
    
    const payload = {
      keyValue: 'PrintInstruction',
      configType: 'Array',
      ResultValue: JSON.stringify(instructions)
    };

    this.configDataService.resetStringCacheConfig(payload)
      .pipe(
        first(),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.printInstructions = instructions;
          this.applyFilters();
          this.alertService.success('Print instructions updated successfully');
          this.closeModal();
          this.isLoading = false;
        },
        error: (error) => {
          this.alertService.error('Failed to update print instructions');
          this.isLoading = false;
        }
      });
  }

  private markFormGroupTouched(): void {
    Object.keys(this.editForm.controls).forEach(key => {
      this.editForm.get(key)?.markAsTouched();
    });
  }

  // Helper methods for template
  getFieldError(fieldName: string): string {
    const control = this.editForm.get(fieldName);
    if (control?.errors && control.touched) {
      if (control.errors['required']) return `${fieldName} is required`;
      if (control.errors['min']) return `Minimum value is ${control.errors['min'].min}`;
      if (control.errors['max']) return `Maximum value is ${control.errors['max'].max}`;
    }
    return '';
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.editForm.get(fieldName);
    return !!(control?.invalid && control.touched);
  }

  getPrintPagesSummary(instruction: PrintInstruction): string {
    const pages = [];
    if (instruction.printPage) pages.push('Page 1');
    if (instruction.printPage2) pages.push('Page 2');
    if (instruction.printPage3) pages.push('Page 3');
    if (instruction.printPageFinance) pages.push('Finance');
    if (instruction.printPageCarCPA) pages.push('Car CPA');
    if (instruction.printPageGeneralCPA) pages.push('General CPA');
    if (instruction.printPageIfcoTokio) pages.push('Iffco Tokio');
    if (instruction.printOnlyFirstPage) pages.push('First Only');
    
    return pages.length > 0 ? pages.join(', ') : 'None';
  }

  getCompanyLogo(company: string): string {
    return this.companyImageMap[company] || 'assets/default-logo.png';
  }

  trackByPolicyType(index: number, item: PrintInstruction): string {
    return item.policyType;
  }
}
